/**
 * `useWidgetData(key, fetcher, { interval, enabled })`
 *
 * Subscribes the widget to a cached, periodically refreshed data key and reports the key's
 * status to the enclosing WidgetFrame, which owns the skeleton / error / stale states.
 * Inside a gallery preview the hook returns mock data (when the registry entry provides it)
 * or whatever is already cached, and never subscribes; locked previews never fetch.
 */
import { useCallback, useEffect, useRef } from 'react';

import { useWidgetFrame } from './frameContext';
import { run, selectEntry, subscribe, useDashboardDataStore } from './storeDashboardData';

const PREVIEW_STATUS = Object.freeze({ loading: false, error: null, updatedAt: null });

export function useWidgetData(key, fetcher, { interval = 'normal', enabled = true } = {}) {
  const frame = useWidgetFrame();
  const inPreview = Boolean(frame?.inPreview);
  const locked = Boolean(frame?.locked);
  const mock = inPreview ? frame?.entry?.preview?.mock?.[key] : undefined;
  const live = enabled && !inPreview && !locked && Boolean(key);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const entry = useDashboardDataStore(selectEntry(key || '__none__'));

  useEffect(() => {
    if (!live) return undefined;
    return subscribe(key, (args) => fetcherRef.current(args), interval);
  }, [key, live, interval]);

  const data = mock !== undefined ? mock : entry.data;
  const loading = live ? entry.data === undefined && !entry.error : false;
  const error = live ? entry.error : null;
  const updatedAt = live ? entry.updatedAt : null;

  const refresh = useCallback(() => (live ? run(key, { force: true }) : Promise.resolve()), [key, live]);

  const reportStatus = frame?.reportStatus;
  const unreportStatus = frame?.unreportStatus;
  useEffect(() => {
    if (!reportStatus || !key) return undefined;
    reportStatus(key, live ? { loading, error, updatedAt, hasData: data !== undefined, refresh } : PREVIEW_STATUS);
    return () => unreportStatus?.(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, live, loading, error, updatedAt, data === undefined, reportStatus, unreportStatus, refresh]);

  return { data, error, loading, refreshing: live ? entry.refreshing : false, updatedAt, refresh };
}

/** Combine several hook results (loading if any lacks data, error if any errored without data). */
export function combineStatus(...results) {
  return {
    loading: results.some((r) => r.loading),
    error: results.find((r) => r.error && r.data === undefined)?.error || null,
    updatedAt: results.reduce((min, r) => (r.updatedAt && (!min || r.updatedAt < min) ? r.updatedAt : min), null),
  };
}
